/**
 * Retirement Insights and Recommendations Generator
 * Generates actionable insights based on retirement plan analysis
 */

/**
 * Generate comprehensive retirement insights
 * @param {Object} analysisData - Analysis data
 * @param {string} country - Country code (US or UK) for currency formatting
 */
export function generateRetirementInsights(analysisData, country = 'US') {
  const {
    metrics,
    // accumulationResult,  // unused
    // drawdownResult,       // unused
    monteCarloResult,
    withdrawalStrategyComparison,
    inputs
  } = analysisData;

  const insights = {
    summary: generateSummary(metrics, inputs),
    warnings: [],
    recommendations: [],
    opportunities: [],
    strengths: []
  };

  // Analyze funding ratio
  if (metrics.fundingRatio < 0.5) {
    insights.warnings.push({
      type: 'critical',
      title: 'Severe Retirement Shortfall',
      message: `Your projected retirement savings are only ${Math.round(metrics.fundingRatio * 100)}% of what you need. You're currently on track to have ${formatCurrency(analysisData.accumulationResult.totalAtRetirement, country)} at retirement, but need ${formatCurrency(metrics.requiredPortfolio, country)} for a sustainable retirement.`,
      icon: '🚨',
      severity: 'critical'
    });

    insights.recommendations.push({
      title: 'Increase Retirement Contributions',
      message: `Consider increasing your annual contributions by ${formatCurrency(calculateRequiredAdditionalContribution(analysisData), country)} to get back on track.`,
      impact: 'high',
      icon: '💰'
    });

    insights.recommendations.push({
      title: 'Delay Retirement',
      message: `Working ${calculateYearsToDelayRetirement(analysisData)} additional years could significantly improve your retirement security.`,
      impact: 'high',
      icon: '📅'
    });
  } else if (metrics.fundingRatio < 0.75) {
    insights.warnings.push({
      type: 'warning',
      title: 'Retirement Underfunded',
      message: `You're ${Math.round((1 - metrics.fundingRatio) * 100)}% short of your retirement goal. Small adjustments now can make a big difference.`,
      icon: '⚠️',
      severity: 'warning'
    });

    insights.recommendations.push({
      title: 'Boost Contributions',
      message: `Increasing your monthly contributions by ${formatCurrency(calculateRequiredAdditionalContribution(analysisData) / 12, country)} could help you reach your goal.`,
      impact: 'medium',
      icon: '📈'
    });
  } else if (metrics.fundingRatio < 1.0) {
    insights.warnings.push({
      type: 'caution',
      title: 'Nearly On Track',
      message: `You're close to your retirement goal (${Math.round(metrics.fundingRatio * 100)}% funded). A few adjustments will get you there.`,
      icon: '⚡',
      severity: 'caution'
    });
  } else {
    insights.strengths.push({
      title: 'On Track for Retirement',
      message: `Your retirement plan is ${Math.round(metrics.fundingRatio * 100)}% funded. You're projected to have ${formatCurrency(metrics.surplus, country)} more than needed!`,
      icon: '✅'
    });

    if (metrics.fundingRatio > 1.5) {
      insights.opportunities.push({
        title: 'Consider Early Retirement',
        message: `With ${Math.round(metrics.fundingRatio * 100)}% funding, you might be able to retire ${calculatePossibleEarlyRetirement(analysisData)} years earlier.`,
        icon: '🎯'
      });
    }
  }

  // Analyze Monte Carlo results
  if (monteCarloResult) {
    if (monteCarloResult.successRate < 50) {
      insights.warnings.push({
        type: 'critical',
        title: 'High Failure Risk',
        message: `Monte Carlo analysis shows only a ${monteCarloResult.successRate}% chance of success. This plan has significant risk.`,
        icon: '📉',
        severity: 'critical'
      });
    } else if (monteCarloResult.successRate < 75) {
      insights.warnings.push({
        type: 'warning',
        title: 'Moderate Retirement Risk',
        message: `Your plan has a ${monteCarloResult.successRate}% success rate. Consider reducing risk or increasing savings.`,
        icon: '⚠️',
        severity: 'warning'
      });

      insights.recommendations.push({
        title: 'Reduce Withdrawal Rate',
        message: `Reducing your initial withdrawal to ${Math.round(metrics.sustainableWithdrawalRate * 0.8 * 10) / 10}% could improve your success rate to over 90%.`,
        impact: 'high',
        icon: '🛡️'
      });
    } else if (monteCarloResult.successRate < 90) {
      insights.warnings.push({
        type: 'caution',
        title: 'Good But Not Great',
        message: `${monteCarloResult.successRate}% success rate is decent, but small improvements could provide more security.`,
        icon: '💡',
        severity: 'caution'
      });
    } else {
      insights.strengths.push({
        title: 'Highly Secure Plan',
        message: `${monteCarloResult.successRate}% success rate indicates a very robust retirement plan.`,
        icon: '🛡️'
      });
    }
  }

  // Analyze withdrawal rate
  if (metrics.sustainableWithdrawalRate > 5) {
    insights.warnings.push({
      type: 'warning',
      title: 'High Withdrawal Rate',
      message: `Your planned withdrawal rate of ${metrics.sustainableWithdrawalRate.toFixed(1)}% is above the safe 4% guideline. Your portfolio may not last through retirement.`,
      icon: '📊',
      severity: 'warning'
    });

    insights.recommendations.push({
      title: 'Reduce Expenses or Increase Savings',
      message: `To achieve a safer 4% withdrawal rate, you need either ${formatCurrency(metrics.requiredPortfolio * 0.25, country)} more in savings or to reduce annual expenses by ${formatCurrency(inputs.desiredAnnualIncome * 0.2, country)}.`,
      impact: 'high',
      icon: '⚖️'
    });
  } else if (metrics.sustainableWithdrawalRate < 3) {
    insights.strengths.push({
      title: 'Conservative Withdrawal Rate',
      message: `Your ${metrics.sustainableWithdrawalRate.toFixed(1)}% withdrawal rate is very conservative, providing extra security.`,
      icon: '🎯'
    });

    insights.opportunities.push({
      title: 'Room for Higher Spending',
      message: `You could safely increase your retirement income by ${formatCurrency((0.04 - metrics.sustainableWithdrawalRate / 100) * analysisData.accumulationResult.totalAtRetirement, country)} per year.`,
      icon: '💎'
    });
  }

  // Analyze income replacement ratio
  if (metrics.incomeReplacementRatio < 0.7) {
    insights.warnings.push({
      type: 'caution',
      title: 'Low Income Replacement',
      message: `Your retirement income will be ${Math.round(metrics.incomeReplacementRatio * 100)}% of your current income. Most experts recommend 70-80%.`,
      icon: '💵',
      severity: 'caution'
    });
  } else if (metrics.incomeReplacementRatio > 1.0) {
    insights.strengths.push({
      title: 'Excellent Income Replacement',
      message: `Your retirement income (${Math.round(metrics.incomeReplacementRatio * 100)}% of current) exceeds your pre-retirement income!`,
      icon: '💰'
    });
  }

  // Analyze withdrawal strategy
  if (withdrawalStrategyComparison) {
    const bestStrategy = findBestWithdrawalStrategy(withdrawalStrategyComparison);
    if (bestStrategy && bestStrategy !== inputs.withdrawalStrategy) {
      insights.recommendations.push({
        title: 'Optimize Withdrawal Strategy',
        message: `The "${formatStrategyName(bestStrategy)}" strategy may provide better results for your situation.`,
        impact: 'medium',
        icon: '🎲'
      });
    }
  }

  // Time-based recommendations
  const yearsToRetirement = inputs.retirementAge - inputs.currentAge;
  if (yearsToRetirement > 20) {
    insights.recommendations.push({
      title: 'Maximize Growth Potential',
      message: `With ${yearsToRetirement} years until retirement, consider a more aggressive asset allocation to maximize growth.`,
      impact: 'medium',
      icon: '📈'
    });
  } else if (yearsToRetirement < 10 && yearsToRetirement > 0) {
    insights.recommendations.push({
      title: 'Reduce Portfolio Risk',
      message: `With only ${yearsToRetirement} years to retirement, consider shifting to a more conservative allocation to protect your savings.`,
      impact: 'medium',
      icon: '🛡️'
    });
  }

  // State pension / Social Security insights
  if (!inputs.statePension || inputs.statePension === 0) {
    insights.opportunities.push({
      title: 'Verify State Pension / Social Security',
      message: `Make sure to account for state pension (UK) or Social Security (US) benefits in your plan. This could add significant guaranteed income.`,
      icon: '🏛️'
    });
  } else {
    const pensionPercentage = (inputs.statePension / inputs.desiredAnnualIncome) * 100;
    insights.strengths.push({
      title: 'State Pension Benefit',
      message: `Your state pension will cover ${Math.round(pensionPercentage)}% of your retirement income needs.`,
      icon: '🏛️'
    });
  }

  // NEW: Check for missing income sources and suggest them
  const hasRentalIncome = inputs.rentalProperties && inputs.rentalProperties.length > 0;
  const hasPartTimeIncome = inputs.partTimeWork !== null;
  const hasAnnuities = inputs.annuities && inputs.annuities.length > 0;
  const hasOtherIncome = inputs.otherIncome && inputs.otherIncome.length > 0;
  const hasSpouseIncome = inputs.spouseIncome && inputs.spouseIncome.included;

  if (!hasRentalIncome && !hasPartTimeIncome && !hasAnnuities && !hasOtherIncome) {
    insights.opportunities.push({
      title: 'Consider Additional Income Sources',
      message: `Your plan relies solely on portfolio withdrawals and state benefits. Consider adding rental properties, part-time work, or annuities to diversify your retirement income and reduce portfolio withdrawal pressure.`,
      icon: '💡'
    });
  }

  // Rental income insights
  if (hasRentalIncome) {
    const totalRentalIncome = inputs.rentalProperties.reduce((sum, prop) => {
      const annualRent = prop.monthlyRent * 12;
      const effectiveRent = annualRent * (1 - prop.vacancyRate);
      return sum + Math.max(0, effectiveRent - prop.annualExpenses);
    }, 0);

    insights.strengths.push({
      title: 'Rental Income Included',
      message: `Your rental properties provide approximately ${formatCurrency(totalRentalIncome, country)}/year in net income, reducing your portfolio withdrawal needs by ${Math.round((totalRentalIncome / inputs.desiredAnnualIncome) * 100)}%.`,
      icon: '🏠'
    });
  } else {
    insights.opportunities.push({
      title: 'Rental Property Income Not Considered',
      message: `If you own or plan to own rental properties, add them to your plan. Rental income can significantly reduce your reliance on portfolio withdrawals.`,
      icon: '🏠'
    });
  }

  // Part-time work insights
  if (hasPartTimeIncome) {
    const workYears = inputs.partTimeWork.endAge - inputs.partTimeWork.startAge;
    insights.strengths.push({
      title: 'Part-Time Work Income',
      message: `Working part-time for ${workYears} years (ages ${inputs.partTimeWork.startAge}-${inputs.partTimeWork.endAge}) provides ${formatCurrency(inputs.partTimeWork.annualAmount, country)}/year, giving your portfolio time to grow.`,
      icon: '💼'
    });
  } else if (yearsToRetirement < 10) {
    insights.opportunities.push({
      title: 'Consider Phased Retirement',
      message: `Working part-time for the first few years of retirement can significantly improve your plan's success rate. Even ${formatCurrency(20000, country)}/year for 5 years can make a big difference.`,
      icon: '💼'
    });
  }

  // Annuity insights
  if (hasAnnuities) {
    const totalAnnuityIncome = inputs.annuities.reduce((sum, ann) => sum + ann.annualPayment, 0);
    insights.strengths.push({
      title: 'Guaranteed Annuity Income',
      message: `Your annuities provide ${formatCurrency(totalAnnuityIncome, country)}/year in guaranteed income, covering ${Math.round((totalAnnuityIncome / inputs.desiredAnnualIncome) * 100)}% of your expenses.`,
      icon: '📜'
    });
  } else if (metrics.fundingRatio < 0.85 && inputs.currentAge > 55) {
    insights.opportunities.push({
      title: 'Consider Purchasing an Annuity',
      message: `An annuity could provide guaranteed income to cover essential expenses, reducing sequence-of-returns risk. Consider using ${Math.round((inputs.essentialSpending * 10) / 1000) * 1000} to purchase guaranteed income.`,
      icon: '📜'
    });
  }

  // Other passive income insights
  if (hasOtherIncome) {
    const totalOtherIncome = inputs.otherIncome.reduce((sum, inc) => sum + inc.annualAmount, 0);
    insights.strengths.push({
      title: 'Additional Passive Income',
      message: `Your other income sources (${inputs.otherIncome.map(i => i.description).join(', ')}) provide ${formatCurrency(totalOtherIncome, country)}/year.`,
      icon: '💰'
    });
  }

  // Tax optimization opportunities
  if (inputs.country === 'UK') {
    // UK Tax-Free Lump Sum insights
    const ukLumpSumOptions = inputs.ukTaxFreeLumpSumOptions || { takeAsLumpSum: true };
    if (ukLumpSumOptions.takeAsLumpSum) {
      insights.opportunities.push({
        title: 'UK Tax-Free Lump Sum',
        message: `Don't forget you can take 25% of your pension as a tax-free lump sum at retirement (${formatCurrency(analysisData.accumulationResult.totalAtRetirement * 0.25, country)}).`,
        icon: '🇬🇧'
      });
    } else {
      insights.strengths.push({
        title: 'Phased UK Tax-Free Withdrawals',
        message: `Taking your 25% tax-free lump sum over ${ukLumpSumOptions.phasedYears} years can be more tax-efficient than taking it all at once. Smart planning!`,
        icon: '🇬🇧'
      });

      insights.recommendations.push({
        title: 'Coordinate Tax-Free Withdrawals',
        message: `Plan your phased tax-free withdrawals alongside your regular income to minimize tax liability each year.`,
        impact: 'medium',
        icon: '📊'
      });
    }
  } else if (inputs.country === 'US') {
    if (inputs.currentAge < 50) {
      insights.opportunities.push({
        title: 'Roth Conversion Opportunity',
        message: `Consider Roth IRA conversions to create tax-free retirement income and avoid future RMDs.`,
        icon: '🇺🇸'
      });
    }

    if (inputs.retirementAge < 73) {
      insights.recommendations.push({
        title: 'Plan for Required Minimum Distributions',
        message: `At age 73, you'll need to take RMDs from traditional retirement accounts. Factor this into your withdrawal strategy.`,
        impact: 'medium',
        icon: '📋'
      });
    }
  }

  return insights;
}

/**
 * Generate executive summary
 */
function generateSummary(metrics, inputs) {
  const yearsToRetirement = inputs.retirementAge - inputs.currentAge;
  const status = metrics.fundingRatio >= 1.0 ? 'on track' :
                 metrics.fundingRatio >= 0.75 ? 'nearly on track' :
                 metrics.fundingRatio >= 0.5 ? 'underfunded' : 'severely underfunded';

  return {
    status,
    fundingPercentage: Math.round(metrics.fundingRatio * 100),
    yearsToRetirement,
    incomeReplacementPercentage: Math.round(metrics.incomeReplacementRatio * 100),
    withdrawalRate: metrics.sustainableWithdrawalRate.toFixed(1)
  };
}

/**
 * Calculate required additional annual contribution
 */
function calculateRequiredAdditionalContribution(analysisData) {
  const { metrics, inputs /* , accumulationResult */ } = analysisData;
  const shortfall = metrics.shortfall;
  const yearsToRetirement = inputs.retirementAge - inputs.currentAge;

  if (yearsToRetirement <= 0) return 0;

  // Future value of annuity formula (simplified)
  // We need to find PMT where FV = PMT * [(1+r)^n - 1] / r
  const r = 0.07; // 7% expected return
  const n = yearsToRetirement;
  const fvFactor = (Math.pow(1 + r, n) - 1) / r;

  return shortfall / fvFactor;
}

/**
 * Calculate years to delay retirement
 */
function calculateYearsToDelayRetirement(analysisData) {
  const { metrics } = analysisData;
  const shortfall = metrics.shortfall;
  const requiredPortfolio = metrics.requiredPortfolio;

  // Simplified: each additional year adds ~10-15% to retirement savings
  const growthPerYear = 0.12; // 12% (contributions + growth)
  const yearsNeeded = Math.log(1 + (shortfall / requiredPortfolio)) / Math.log(1 + growthPerYear);

  return Math.ceil(yearsNeeded);
}

/**
 * Calculate possible early retirement years
 */
function calculatePossibleEarlyRetirement(analysisData) {
  const { metrics, inputs } = analysisData;
  const surplus = metrics.surplus;
  const annualExpenses = inputs.desiredAnnualIncome - (inputs.statePension || 0);

  // Conservative estimate: surplus / (annual expenses * 1.5)
  const yearsEarlier = Math.floor((surplus / annualExpenses) / 1.5);

  return Math.min(yearsEarlier, 5); // Cap at 5 years
}

/**
 * Find best withdrawal strategy from comparison
 */
function findBestWithdrawalStrategy(comparison) {
  let bestStrategy = null;
  let bestScore = -Infinity;

  Object.entries(comparison).forEach(([strategy, result]) => {
    // Score based on: final balance (50%), success rate (30%), low volatility (20%)
    const balanceScore = result.finalBalance / 1000000; // Normalize
    const volatilityScore = -result.volatility / 10000; // Lower is better
    const score = balanceScore + volatilityScore;

    if (score > bestScore) {
      bestScore = score;
      bestStrategy = strategy;
    }
  });

  return bestStrategy;
}

/**
 * Format strategy name for display
 */
function formatStrategyName(strategy) {
  const names = {
    fixed: 'Fixed Amount',
    percentage: 'Fixed Percentage (4% Rule)',
    inflationAdjusted: 'Inflation-Adjusted',
    guardrails: 'Dynamic Guardrails',
    dynamic: 'Dynamic Based on Portfolio',
    essentialOnly: 'Essential Spending Only'
  };

  return names[strategy] || strategy;
}

/**
 * Format currency for display
 * @param {number} amount - Amount to format
 * @param {string} country - Country code (US or UK)
 */
function formatCurrency(amount, country = 'US') {
  const currencyConfig = {
    'US': { locale: 'en-US', currency: 'USD' },
    'UK': { locale: 'en-GB', currency: 'GBP' }
  };

  const config = currencyConfig[country] || currencyConfig['US'];

  return new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency: config.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

/**
 * Generate action items based on insights
 */
export function generateActionItems(insights) {
  const actions = [];

  // Priority 1: Critical warnings
  insights.warnings
    .filter(w => w.severity === 'critical')
    .forEach(warning => {
      actions.push({
        priority: 1,
        category: 'urgent',
        title: warning.title,
        description: warning.message,
        icon: warning.icon
      });
    });

  // Priority 2: High-impact recommendations
  insights.recommendations
    .filter(r => r.impact === 'high')
    .forEach(rec => {
      actions.push({
        priority: 2,
        category: 'important',
        title: rec.title,
        description: rec.message,
        icon: rec.icon
      });
    });

  // Priority 3: Other warnings
  insights.warnings
    .filter(w => w.severity !== 'critical')
    .forEach(warning => {
      actions.push({
        priority: 3,
        category: 'warning',
        title: warning.title,
        description: warning.message,
        icon: warning.icon
      });
    });

  // Priority 4: Medium-impact recommendations
  insights.recommendations
    .filter(r => r.impact === 'medium')
    .forEach(rec => {
      actions.push({
        priority: 4,
        category: 'recommendation',
        title: rec.title,
        description: rec.message,
        icon: rec.icon
      });
    });

  // Priority 5: Opportunities
  insights.opportunities.forEach(opp => {
    actions.push({
      priority: 5,
      category: 'opportunity',
      title: opp.title,
      description: opp.message,
      icon: opp.icon
    });
  });

  return actions.sort((a, b) => a.priority - b.priority);
}

const retirementInsights = {
  generateRetirementInsights,
  generateActionItems,
  formatStrategyName,
  formatCurrency
};

export default retirementInsights;
